import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
} from "react";
import { styled } from "@mui/material/styles";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";

// Wraps `react-phone-input-2` with the same dropdown config used on /login
// (MobileSignIn). Standardised here so signup + login share the same
// country-picker UX: preferred countries at top, search box, taller
// dropdown, dark theme.
//
// Replaces the previous custom MUI Select dropdown (CountryDropdown.tsx)
// + the two-step "pick country, then type number" flow. The flag picker
// is now embedded in the input field; the country list opens on flag tap.
//
// PM5 T1 (chrome, batch "common") — converted from MUI v4 + `makeStyles` to
// MUI v5+ `styled`. See docs/mui-conversion-guide.md.

// was: useStyles().phoneInput — handed to `react-phone-input-2`'s `inputClass`,
// which appends it to the library's own `form-control` class on the <input>.
// A bare class name is the only thing that prop accepts, so the rule keeps a
// stable className and is declared from the wrapper below (guide §1.2b).
const PHONE_INPUT_CLASS = "TwoStepPhoneInput-input";

// was: useStyles().formFieldSet — the target is a bare <div>, which has no `sx`
// prop, so this is `styled()` (guide §1.2 row 2).
const FormFieldSet = styled("div")(({ theme }) => ({
  margin: theme.spacing(2, 0, 0.5, 0),
  "& label": {
    color: theme.palette.common.white,
  },
  // was the separate `phoneInput` rule, applied to the same <input> element via
  // `inputClass`. Declared here as a descendant selector so it stays one
  // stylesheet; it still carries `!important`, so it wins over the `width: 100%`
  // in the rule below exactly as it did in v4.
  [`& .${PHONE_INPUT_CLASS}`]: {
    width: "inherit !important",
  },
  "& .react-tel-input .form-control": {
    backgroundColor: "#444",
    color: "#fff",
    borderColor: "#444 !important",
    width: "100%",
  },
  "& .react-tel-input .form-control+div:before": {
    color: "#fff !important",
    backgroundColor: theme.palette.background.ffNavy,
  },
  "& .react-tel-input .arrow": {
    borderTop: "4px solid #fff !important",
  },
  // Dark-theme overrides for the country dropdown so it matches the
  // input field instead of defaulting to react-phone-input-2's white
  // material.css palette.
  "& .react-tel-input .country-list": {
    backgroundColor: "#444",
    color: "#fff",
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(255, 255, 255, 0.5) transparent",
  },
  "& .react-tel-input .country-list .country": {
    color: "#fff",
  },
  "& .react-tel-input .country-list .country .dial-code": {
    color: "#bbb",
  },
  "& .react-tel-input .country-list .country:hover": {
    backgroundColor: "#555",
  },
  "& .react-tel-input .country-list .country.highlight": {
    backgroundColor: "#555",
  },
  "& .react-tel-input .country-list .divider": {
    borderBottom: "1px solid #666",
  },
  "& .react-tel-input .country-list .search": {
    backgroundColor: "#444",
    borderBottom: "1px solid #666",
  },
  "& .react-tel-input .country-list .search-box": {
    backgroundColor: "#555 !important",
    color: "#fff !important",
    borderColor: "#666 !important",
  },
  "& .react-tel-input .country-list .search-emoji": {
    color: "#fff",
  },
  "& .react-tel-input .country-list .no-entries-message": {
    color: "#bbb",
  },
  "& .react-tel-input .country-list::-webkit-scrollbar": {
    width: "8px",
    WebkitAppearance: "none",
  },
  "& .react-tel-input .country-list::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(255, 255, 255, 0.5)",
    borderRadius: "4px",
  },
  "& .react-tel-input .country-list::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
}));

type TwoStepPhoneInputProps = {
  onChange: (countryCode: string, phoneNumber: string) => void;
  onFocus?: () => void;
  disabled?: boolean;
};

// Imperative API exposed to consumers that need to seed the field
// programmatically (e.g. applying a #284 phone-validation suggestion).
// We deliberately do NOT pass a ref via `inputProps.ref` — that would
// override react-phone-input-2's own internal ref and crash the library
// when it tries to focus / move the cursor. Instead we find the input
// via querySelector on a wrapper div.
export type TwoStepPhoneInputHandle = {
  seedValue: (e164OrDigits: string) => void;
};

const TwoStepPhoneInput = forwardRef<
  TwoStepPhoneInputHandle,
  TwoStepPhoneInputProps
>(({ onChange, onFocus, disabled }, ref) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const handleChange = useCallback(
    (value: string, data: { dialCode?: string }) => {
      const dialCode = data.dialCode || "";
      onChange(dialCode, value.substring(dialCode.length));
    },
    [onChange],
  );

  useImperativeHandle(
    ref,
    () => ({
      seedValue: (e164OrDigits: string) => {
        const digits = e164OrDigits.replace(/^\+/, "");
        const input = wrapperRef.current?.querySelector<HTMLInputElement>(
          'input[name="mobileNumber"]',
        );
        if (!input) return;
        const setter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          "value",
        )?.set;
        setter?.call(input, digits);
        input.dispatchEvent(new Event("input", { bubbles: true }));
      },
    }),
    [],
  );

  return (
    <FormFieldSet ref={wrapperRef}>
      <PhoneInput
        inputClass={PHONE_INPUT_CLASS}
        inputProps={{
          name: "mobileNumber",
          required: true,
          autoFocus: true,
          // Tell the browser what this field is. Without it, autofill falls
          // back to heuristics on `name`/`type` and fills whatever it has
          // stored for the origin. "tel" is right for both consumers (login
          // and signup) — it is the number itself, not a country-specific part.
          autoComplete: "tel",
        }}
        country="gb"
        // The field is seeded with "+44" and typing APPENDS to it, so a member
        // entering "07400123456" or "+447400123456" produces a value longer
        // than the GB mask. By default the library silently TRUNCATES at the
        // mask length, which destroyed evidence before anything could act on
        // it: the #284 validator strips a duplicated calling code and offers a
        // "Use +44…" button, but on a truncated value the remainder is a short
        // fragment that cannot validate, so the suggestion never fired and the
        // member got a generic "couldn't recognise it" instead of a one-tap fix.
        // Keeping the digits lets the validator see what was actually entered.
        enableLongNumbers
        placeholder="Mobile Number"
        aria-label="Mobile Number"
        enableTerritories={true}
        countryCodeEditable={false}
        enableSearch={true}
        searchPlaceholder="Search countries"
        dropdownStyle={{ maxHeight: 400 }}
        preferredCountries={["gb", "ie", "us", "au", "nz"]}
        excludeCountries={["je"]}
        onChange={handleChange}
        onFocus={onFocus}
        disabled={disabled}
      />
    </FormFieldSet>
  );
});

TwoStepPhoneInput.displayName = "TwoStepPhoneInput";

export default TwoStepPhoneInput;
